
import { GoogleGenAI, Modality } from "@google/genai";
import { getSystemInstruction } from "../constants";
import { Dialect, Attachment, Language } from "../types";

/**
 * Generates a text response using Gemini 3 Flash model.
 */
export const generateResponse = async (
  prompt: string,
  dialect: Dialect,
  history: { role: string; content: string }[],
  attachments?: Attachment[],
  lang: Language = 'ar',
  apiKey?: string
): Promise<string> => {
  const key = apiKey || process.env.API_KEY;
  if (!key) throw new Error(lang === 'ar' ? "مفتاح الربط مفقود" : "API Key missing");

  const ai = new GoogleGenAI({ apiKey: key });

  const contents = history.map((h) => ({
    role: h.role === "user" ? "user" : "model",
    parts: [{ text: h.content }],
  }));

  const userParts: any[] = [{ text: prompt }];

  if (attachments && attachments.length > 0) {
    attachments.forEach((file) => {
      if (file.type.startsWith("image/")) {
        userParts.push({
          inlineData: {
            mimeType: file.type,
            data: file.data.split(",")[1], // Remove data URI prefix
          },
        });
      }
    });
  }

  contents.push({
    role: "user",
    parts: userParts,
  });

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents,
      config: {
        systemInstruction: getSystemInstruction(dialect, lang),
        temperature: 0.7,
      },
    });

    return response.text || "";
  } catch (error) {
    console.error("Gemini API Error:", error);
    throw new Error(lang === 'ar' ? "حدث خطأ أثناء التواصل مع المساعد." : "An error occurred while communicating with the assistant.");
  }
};

/**
 * Transforms text input into audio using Gemini 2.5 Flash TTS.
 */
export const generateAudio = async (text: string, voiceName: string = 'Kore', apiKey?: string): Promise<string | undefined> => {
  const key = apiKey || process.env.API_KEY;
  if (!key) return undefined;

  const ai = new GoogleGenAI({ apiKey: key });
  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName: voiceName as any },
          },
        },
      },
    });
    return response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
  } catch (error) {
    console.error("Gemini TTS Error:", error);
    throw new Error("TTS generation error.");
  }
};